/**
 * Public /enrol/success — post-Stripe redirect landing.
 * Polls /api/enrol/{id}/status until finalisation completes, then AUTO-LOGS
 * the customer straight into /portal using the JWT the backend returns —
 * no round-trip through /login. If the auto-login can't take (browser
 * blocked cookies etc.), we surface a manual "Sign in with your password"
 * escape hatch as well.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Brand from "@/components/Brand";
import { setAuthToken } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EnrolSuccess() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const enrolmentId = params.get("enrolment_id");
    const [state, setState] = useState({ status: "checking", email: null, token: null });
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        if (!enrolmentId) return;
        let cancelled = false;
        let tries = 0;

        async function poll() {
            try {
                const { data } = await axios.get(
                    `${API_URL}/api/enrol/${enrolmentId}/status`,
                    { withCredentials: true },
                );
                if (cancelled) return;
                setState({
                    status: data.status,
                    email: data.user_email,
                    token: data.auto_login_token || null,
                });
                if (data.status === "completed") {
                    if (data.auto_login_token) {
                        setAuthToken(data.auto_login_token);
                        setRedirecting(true);
                        // Small pause so the "You're in" flash is visible.
                        setTimeout(() => nav("/portal", { replace: true }), 900);
                    }
                    return;
                }
                if (tries > 40) return;
            } catch { /* ignore */ }
            tries += 1;
            if (!cancelled) setTimeout(poll, 1500);
        }
        poll();
        localStorage.removeItem("enrol_id:sole_trader");
        localStorage.removeItem("enrol_id:company_2owner");
        return () => { cancelled = true; };
    }, [enrolmentId, nav]);

    const done = state.status === "completed";

    return (
        <div className="min-h-screen bg-[#F5F4EF]">
            <header className="mx-auto max-w-3xl px-4 py-6 flex items-center justify-between">
                <Brand size="sm" data-testid="enrol-success-brand" />
            </header>

            <main className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="enrol-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#0F1115] text-white text-[26px] leading-[56px]">✓</div>
                <h1 className="mt-6 text-[28px] font-semibold text-[#0F1115]">
                    {done && redirecting ? "You're in — opening your workspace…" : done ? "You're signed up." : "Setting up your workspace…"}
                </h1>
                <p className="mt-3 text-[15px] text-[#1F2328] leading-6">
                    {done
                        ? "Your Authority to Act is signed and stored, and your ClientOps workspace is ready. A NZ Taxation team member will call or email you within 24 hours to welcome you and confirm next steps — your first invoice will follow after that."
                        : "We're just spinning up your ClientOps workspace — this usually takes a couple of seconds."}
                </p>

                {done && state.email && (
                    <div className="mt-6 inline-block rounded-lg bg-white border border-[#E4E4E7] px-4 py-3 text-[13px] text-[#1F2328]" data-testid="enrol-success-email">
                        Signed up as <b>{state.email}</b>
                    </div>
                )}

                <div className="mt-8 flex flex-col items-center gap-3">
                    <button
                        onClick={() => {
                            if (state.token) setAuthToken(state.token);
                            nav("/portal", { replace: true });
                        }}
                        disabled={!done}
                        className="rounded-full bg-[#0F1115] text-white px-6 py-3 text-[14px] font-medium hover:bg-[#1F2328] disabled:opacity-40"
                        data-testid="enrol-success-open-portal"
                    >
                        {done ? "Open your ClientOps workspace →" : "Setting up…"}
                    </button>
                    {done && (
                        <Link
                            to="/login"
                            className="text-[12px] text-[#5E636E] hover:text-[#0F1115] underline"
                            data-testid="enrol-success-signin-fallback"
                        >
                            Or sign in manually with the password you chose
                        </Link>
                    )}
                </div>

                <div className="mt-8 text-[12px] text-[#5E636E]">
                    A signed PDF copy of your Authority to Act is already in your ClientOps vault.
                </div>
            </main>

            <footer className="border-t border-[#E4E4E7] bg-white">
                <div className="mx-auto max-w-3xl px-4 py-6 text-[12px] text-[#5E636E] flex flex-wrap gap-x-4 gap-y-2 justify-between">
                    <span>© NZ Taxation Limited t/a ClientOps</span>
                    <Link to="/privacy" className="hover:text-[#0F1115]">Privacy</Link>
                    <Link to="/support" className="hover:text-[#0F1115]">Support</Link>
                </div>
            </footer>
        </div>
    );
}
